import { useMemo } from 'react';
import { Medal, Trophy } from 'lucide-react';
import Skeleton from '../ui/Skeleton';
import SearchBar from '../ui/SearchBar';
import ContributorRowAvatar from './ContributorRowAvatar';
import {
  contributorProgressFill,
  contributorProgressTrack,
  leaderboardRankClass,
  leaderboardRowClass,
  memberNameClass,
  teamSectionTitleClass,
} from '../members/memberViewTheme';
import { formatCompactNumber } from '../../utils/formatNumber';

function formatLastCommitDate(iso: string | null | undefined) {
  if (iso == null || iso === '') return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function ContributorsBarChartSkeleton({ rows = 8 }: { rows?: number }) {
  return (
    <ul className="divide-y divide-dark-border list-none m-0 p-0" aria-hidden>
      {Array.from({ length: rows }, (_, i) => `contributor-skel-row-${i}`).map((skelKey) => (
        <li key={skelKey} className="px-4 py-3 flex items-center gap-3">
          <Skeleton className="h-4 w-5 shrink-0" />
          <Skeleton className="h-7 w-7 shrink-0 rounded-full" />
          <div className="shrink-0 space-y-1.5 min-w-[7rem] flex-1 sm:flex-none sm:w-48">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-28" />
          </div>
          <Skeleton className="h-4 flex-1 rounded-md min-w-0" />
          <div className="hidden sm:flex shrink-0 gap-2">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-4 w-10" />
          </div>
        </li>
      ))}
    </ul>
  );
}

type ContributorRow = Record<string, unknown> & {
  _rowKey?: string;
  displayName?: string;
  displayEmail?: string;
  totalCommits?: number;
  pullRequests?: number;
  linesAdded?: number;
  linesDeleted?: number;
  lastCommitDate?: string | null;
};

/**
 * Contributor leaderboard with optional top-3 podium and single-bar rows.
 */
export default function ContributorsBarChartCard({
  contributorRows,
  loading,
  listKey,
  title = 'Contributors',
  emptyMessage = 'No contributors found.',
  timeframe,
  status,
  onTimeframeChange,
  onStatusChange,
  search = '',
  onSearchChange,
}: {
  contributorRows: ContributorRow[];
  loading: boolean;
  listKey: string;
  title?: string;
  emptyMessage?: string;
  timeframe?: string;
  status?: string;
  onTimeframeChange?: (value: string) => void;
  onStatusChange?: (value: string) => void;
  search?: string;
  onSearchChange?: (value: string) => void;
}) {
  const effectiveTimeframe = timeframe || 'week';
  const effectiveStatus = status || 'active';
  const rows = useMemo(() => {
    const copy = [...contributorRows];
    copy.sort((a, b) => Number(b.totalCommits) - Number(a.totalCommits));
    return copy;
  }, [contributorRows]);

  const maxCommits = useMemo(
    () => rows.reduce((m, r) => Math.max(m, Number(r.totalCommits) || 0), 0),
    [rows]
  );

  const showPodium = rows.length >= 3;
  const podiumRows = showPodium ? rows.slice(0, 3) : [];
  const restRows = showPodium ? rows.slice(3) : rows;

  return (
    <div className="card-mdl-static min-w-0 overflow-hidden">
      <header className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 border-b border-dark-border bg-dark-elevated px-4 py-3 overflow-hidden">
        <h3 className={`${teamSectionTitleClass} min-w-0 truncate`}>
          {title}{' '}
          <span className="text-slate-500 font-normal tabular-nums">({contributorRows.length})</span>
        </h3>

        <div
          className="flex min-w-0 max-w-full flex-nowrap items-center justify-end gap-1.5 justify-self-end"
          role="toolbar"
          aria-label="Search and filter contributors"
        >
          {onSearchChange ? (
            <SearchBar
              value={search}
              onChange={onSearchChange}
              placeholder="Search…"
              variant="compact"
              className="h-9 w-[6.5rem] min-w-0 shrink-0"
            />
          ) : null}

          <label className="sr-only" htmlFor={`contributors-timeframe-${listKey}`}>
            Timeframe
          </label>
          <select
            id={`contributors-timeframe-${listKey}`}
            className="select-chart-toolbar !w-[5.5rem]"
            value={effectiveTimeframe}
            onChange={(e) => onTimeframeChange?.(e.target.value)}
          >
            <option value="week">Week</option>
            <option value="month">Month</option>
            <option value="year">Year</option>
          </select>

          <label className="sr-only" htmlFor={`contributors-status-${listKey}`}>
            Status
          </label>
          <select
            id={`contributors-status-${listKey}`}
            className="select-chart-toolbar !w-[5.5rem]"
            value={effectiveStatus}
            onChange={(e) => onStatusChange?.(e.target.value)}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </header>

      {loading ? (
        <ContributorsBarChartSkeleton key={`contributors-skel-${listKey}`} />
      ) : rows.length === 0 ? (
        <p className="px-4 py-10 text-center text-sm text-slate-500">
          {effectiveStatus === 'inactive' ? 'No inactive contributors found.' : emptyMessage}
        </p>
      ) : (
        <>
          {showPodium ? (
            <div className="grid grid-cols-3 gap-2 px-4 py-4 border-b border-dark-border">
              {[1, 0, 2].map((idx) => {
                const row = podiumRows[idx];
                const rank = idx + 1;
                const isFirst = rank === 1;
                return (
                  <div
                    key={String(row._rowKey)}
                    className={`rounded-xl bg-dark-elevated px-3 py-4 text-center border ${
                      isFirst ? 'border-2 border-amber-400/60' : 'border-dark-border'
                    }`}
                  >
                    {isFirst ? (
                      <Trophy
                        className={`w-5 h-5 mx-auto mb-1 ${leaderboardRankClass(1)}`}
                        aria-hidden
                      />
                    ) : (
                      <Medal
                        className={`w-4 h-4 mx-auto mb-1 ${leaderboardRankClass(rank)}`}
                        aria-hidden
                      />
                    )}
                    <ContributorRowAvatar
                      row={row}
                      size={isFirst ? 'sm' : 'xs'}
                      className="mx-auto mb-1.5"
                    />
                    <div className={`${memberNameClass} truncate text-xs`} title={row.displayName}>
                      {row.displayName}
                    </div>
                    <p
                      className={`mt-1 font-bold tabular-nums text-slate-100 ${isFirst ? 'text-lg' : 'text-sm'}`}
                    >
                      {Number(row.totalCommits) || 0}
                    </p>
                    <p className="text-[10px] text-slate-500">commits</p>
                  </div>
                );
              })}
            </div>
          ) : null}

          {showPodium ? (
            <p className="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              Everyone else
            </p>
          ) : null}

          <ul
            className="divide-y divide-dark-border list-none m-0 p-0"
            key={`contributors-${listKey}`}
          >
            {restRows.map((row, i) => {
              const rank = showPodium ? i + 4 : i + 1;
              const commits = Number(row.totalCommits) || 0;
              const prs = Number(row.pullRequests) || 0;
              const add = Number(row.linesAdded) || 0;
              const del = Number(row.linesDeleted) || 0;
              const netLines = add - del;
              const commitBarPct =
                maxCommits > 0 && commits > 0 ? Math.round((commits / maxCommits) * 1000) / 10 : 0;
              const lastCommitLabel = formatLastCommitDate(row.lastCommitDate);
              const label = `${row.displayName}, ${commits} commit${commits === 1 ? '' : 's'}, ${prs} pull request${prs === 1 ? '' : 's'}, ${netLines >= 0 ? '+' : ''}${netLines} net lines`;
              return (
                <li
                  key={String(row._rowKey)}
                  className={`${leaderboardRowClass} min-w-0 flex-wrap gap-y-2 sm:flex-nowrap`}
                  aria-label={label}
                >
                  <span
                    className={`w-5 shrink-0 text-xs font-semibold tabular-nums ${leaderboardRankClass(rank)}`}
                  >
                    {rank}
                  </span>
                  <ContributorRowAvatar row={row} size="xs" />
                  <div className="min-w-0 flex-1 basis-[40%] sm:basis-auto sm:w-36 lg:w-44">
                    <div className={`${memberNameClass} truncate`} title={row.displayName}>
                      {row.displayName}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {lastCommitLabel ? `Last commit: ${lastCommitLabel}` : 'No last commit date'}
                    </div>
                  </div>
                  <div className="flex min-w-0 flex-[1_1_100%] items-center gap-2 pl-8 sm:flex-1 sm:basis-auto sm:pl-0">
                    <div className="min-w-0 flex-1">
                      <div className={contributorProgressTrack}>
                        {commitBarPct > 0 ? (
                          <div
                            className={contributorProgressFill}
                            style={{ width: `${commitBarPct}%` }}
                          />
                        ) : null}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5 text-[11px] sm:gap-2 sm:text-xs">
                      <span className="whitespace-nowrap font-medium tabular-nums text-brand-purple-light">
                        <span className="sm:hidden">{commits}c</span>
                        <span className="hidden sm:inline">{commits} commits</span>
                      </span>
                      <span className="whitespace-nowrap font-medium tabular-nums text-indigo-300">
                        <span className="sm:hidden">{prs} PR</span>
                        <span className="hidden sm:inline">{prs} PRs</span>
                      </span>
                      <span
                        className={`whitespace-nowrap font-medium tabular-nums ${netLines >= 0 ? 'text-emerald-400' : 'text-red-400'}`}
                      >
                        {netLines >= 0 ? '+' : ''}
                        {formatCompactNumber(netLines)}
                      </span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
